import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Tag,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { publicacoesAPI } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import ItemCard from '../components/ItemCard';
import { getImageUrl } from '../utils/getImageUrl';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const PublicacoesPage = () => {
  const [publicacoes, setPublicacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPublicacao, setEditingPublicacao] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    loadPublicacoes();
  }, [filtro]);

  const loadPublicacoes = async () => {
    setLoading(true);
    try {
      const response = await publicacoesAPI.getAll(filtro);
      setPublicacoes(response.data);
    } catch (error) {
      message.error('Erro ao carregar publicações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPublicacao(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (publicacao) => {
    setEditingPublicacao(publicacao);
    form.setFieldsValue(publicacao);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await publicacoesAPI.delete(id);
      message.success('Publicação deletada com sucesso');
      loadPublicacoes();
    } catch (error) {
      message.error('Erro ao deletar publicação');
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingPublicacao) {
        await publicacoesAPI.update(editingPublicacao._id, values);
        message.success('Publicação atualizada com sucesso');
      } else {
        await publicacoesAPI.create(values);
        message.success('Publicação criada com sucesso');
      }
      setModalVisible(false);
      loadPublicacoes();
    } catch (error) {
      message.error('Erro ao salvar publicação');
      console.error(error);
    }
  };
  const renderPublicacaoImage = (publicacao) => {
    return publicacao.imagem ? (
      <img 
        src={getImageUrl(publicacao.imagem)} 
        alt="Publicação" 
        className="card-image"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    ) : (
      <div className="card-image-placeholder">
        <BookOutlined style={{ fontSize: 24 }} />
      </div>
    );
  };

  const renderPublicacaoContent = (publicacao) => {
    return (
      <div className="card-text">
        {publicacao.texto}
      </div>
    );
  };

  const renderPublicacaoMeta = (publicacao) => {
    return (
      <Tag color={publicacao.produto === 'CSAP' ? 'blue' : 'green'}>
        {publicacao.produto}
      </Tag>
    );
  };
  return (
    <div className="admin-page-container">      <div className="filter-section">
        <Row style={{ width: '100%' }} justify="space-between" align="middle">
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Nova Publicação
            </Button>
          </Col>
          <Col>
            <Select
              placeholder="Filtrar por produto"
              style={{ width: 200 }}
              value={filtro}
              onChange={setFiltro}
              allowClear
            >
              <Option value="CSAP">CSAP</Option>
              <Option value="RHA">RHA</Option>
            </Select>
          </Col>
        </Row>
      </div><Spin spinning={loading}>
        {publicacoes.length === 0 ? (
          <Empty 
            description="Nenhuma publicação encontrada"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="cards-container">
            {publicacoes.map(publicacao => (
              <ItemCard
                key={publicacao._id}
                item={publicacao}
                onEdit={handleEdit}
                onDelete={handleDelete}
                renderImage={renderPublicacaoImage}
                renderContent={renderPublicacaoContent}
                renderMeta={renderPublicacaoMeta}
                showLink={true}
              />
            ))}
          </div>
        )}
      </Spin>

      <Modal
        title={editingPublicacao ? 'Editar Publicação' : 'Nova Publicação'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="modal-form"
        >
          <Form.Item
            name="titulo"
            label="Título"
            rules={[{ required: true, message: 'Digite o título' }]}
          >
            <Input placeholder="Título da publicação" />
          </Form.Item>

          <Form.Item
            name="produto"
            label="Produto"
            rules={[{ required: true, message: 'Selecione o produto' }]}
          >
            <Select placeholder="Selecione o produto">
              <Option value="CSAP">CSAP - Cuidados Saúde Atenção Primária</Option>
              <Option value="RHA">RHA - Reprodução Humana Assistida</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="imagem"
            label="Imagem"
            rules={[{ required: true, message: 'Faça upload da imagem' }]}
          >
            <ImageUpload />
          </Form.Item>

          <Form.Item
            name="texto"
            label="Texto"
            rules={[{ required: true, message: 'Digite o texto' }]}
          >
            <TextArea rows={4} placeholder="Texto da publicação" />
          </Form.Item>

          <Form.Item
            name="link"
            label="Link"
            rules={[
              { required: true, message: 'Digite o link' },
              { type: 'url', message: 'Digite uma URL válida' }
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PublicacoesPage;
